import type {
    Control,
    FieldErrors,
    UseFormRegister,
    UseFormSetValue,
    UseFormTrigger,
    UseFormWatch,
} from 'react-hook-form';

import EInvoicingFields from '@/components/EInvoicingFields';
import type { InvoiceDataSchemaType } from '@/components/steps/InvoicingStep/invoiceDataSchema';
import Input from '@/components/ui/Input';
import { useCountryNames } from '@/hooks/useCountryNames';
import { t } from '@/lib/utils';
import { useUserStore } from '@/stores/userStore';

type InvoicingSectionProps = {
    register: UseFormRegister<InvoiceDataSchemaType>;
    control: Control<InvoiceDataSchemaType>;
    watch: UseFormWatch<InvoiceDataSchemaType>;
    setValue: UseFormSetValue<InvoiceDataSchemaType>;
    trigger: UseFormTrigger<InvoiceDataSchemaType>;
    errors: FieldErrors<InvoiceDataSchemaType>;
};

const InvoicingSection = ({ register, control, errors, watch, setValue, trigger }: InvoicingSectionProps) => {
    const country = watch('invoicing.country');

    const countryNames = useCountryNames();
    const domesticVATCountry = countryNames?.find(item => item.id === country)?.label;

    const { eInvoicingDetails } = useUserStore();
    const hasEInvoicing = eInvoicingDetails && eInvoicingDetails.length > 0;

    return (
        <div className='margin-bottom-15 margin-top-5 rounded bg-white padding-25'>
            <div className='text-bold text-size-18 padding-bottom-15'>{t('onboarding.invoicingDetails.title')}</div>
            <div className='padding-bottom-15'>{t('onboarding.invoicingDetails.description')}</div>

            <Input
                required
                className='flex-1-1-0 margin-bottom-5'
                label={t('general.invoiceEmailAddress')}
                error={errors?.invoicing?.invoiceEmailAddress?.message}
                inputAttributes={{ ...register('invoicing.invoiceEmailAddress') }}
            />
            <div className='display-flex align-items-center margin-bottom-15'>
                <span className='uikit-icon icon-info-sign' />
                <i className='margin-left-5'>{t('general.beAwareThatAllInvoicesWillBeSent')}</i>
            </div>

            <div className='display-flex gap-15 padding-bottom-15'>
                <Input
                    className='flex-basis-33pct'
                    label={t('general.domesticVATCountry')}
                    inputAttributes={{
                        ...register('invoicing.country'),
                        value: domesticVATCountry,
                        disabled: true,
                    }}
                />
                <Input
                    className='flex-1-1-0'
                    label={t('general.domesticVATNumber')}
                    error={errors?.invoicing?.vat?.message}
                    inputAttributes={{
                        ...register('invoicing.vat'),
                    }}
                />
            </div>
            {hasEInvoicing && (
                <EInvoicingFields
                    control={control}
                    register={register}
                    watch={watch}
                    setValue={setValue}
                    trigger={trigger}
                    schemeIdName='invoicing.schemeId'
                    participantIdentifierName='invoicing.participantIdentifier'
                    vatName='invoicing.vat'
                    schemeIdError={errors.invoicing?.schemeId?.message}
                    participantIdentifierError={errors.invoicing?.participantIdentifier?.message}
                />
            )}
        </div>
    );
};

export default InvoicingSection;
